/**
 * The counting trainer, played entirely in this tab.
 *
 * The shape: read the method, then get a position with one square ringed and
 * answer two questions about it — *how many empty squares are under it*, and
 * *whose is it* — before the board shows its working. Then play a move and
 * watch the answer change.
 *
 * Two decisions the rest of the file follows from:
 *
 *  - **Nothing is judged against a stored answer.** Both questions are settled
 *    by counting the board on screen, through the same functions that drew it.
 *    The hunted position carries a count only so a test can check the two agree.
 *  - **The third step is the one that teaches.** Being told the rule and being
 *    made to apply it once produces a player who counts a square, decides it is
 *    theirs, and never counts it again. Playing one disc and watching the square
 *    change hands is the whole reason parity is a habit rather than a fact.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import {
  fillLadder,
  gapsBeneath,
  opponentOf,
  ownerOf,
  parityMap,
  replayParity,
  rowRuleOwner,
} from '../../../games/analysis/parity.ts';
import type { LadderStep, ParityPosition, ParitySquare } from '../../../games/analysis/parity.ts';
import { applyMove, createInitial, legalMoves } from '../../../games/spec/interpreter.ts';
import type { SeatOf, State } from '../../../games/spec/interpreter.ts';
import type { GameSpec, RulesSpec } from '../../../games/spec/schema.ts';
import type { Seat } from '../../../shared/domain.ts';
import type { FindRequest, ParityResponse } from './protocol.ts';

type Rules = RulesSpec;

/** How many positions make a run. Enough to meet an even one and an odd one. */
export const POSITIONS_PER_RUN = 4;

export type ParityPhase =
  /** The method itself, before any board. */
  | 'reading'
  /** Hunting for a position. */
  | 'seeking'
  /** A square is ringed and the count has been asked for. */
  | 'counting'
  /** The count is in; now, whose square is it. */
  | 'owning'
  /** Both answered. The ladder is showing its working. */
  | 'proving'
  /** Free play on the same position, to watch the count move. */
  | 'playing'
  /** All the positions are done. */
  | 'finished'
  /** Nothing could be found. */
  | 'stuck';

export interface ParityView {
  readonly spec: GameSpec;
  readonly phase: ParityPhase;
  readonly board: readonly number[];
  /** The seat the questions are asked of. Stays put while the turn moves on. */
  readonly yourSeat: Seat | null;
  /** Whose turn it is now, which is the thing the tempo step changes. */
  readonly toMove: Seat | null;
  readonly legalColumns: readonly number[];
  /** The square being counted. */
  readonly target: number | null;
  readonly targetRow: number | null;
  readonly targetCol: number | null;
  /** Counted from the board as it stands, so it moves when a disc lands. */
  readonly gaps: number | null;
  readonly owner: Seat | null;
  /** Who the count gave it to before any exploring, for the comparison. */
  readonly openingOwner: Seat | null;
  /** Set once exploring has filled the square: who actually took it. */
  readonly takenBy: Seat | null;
  /** Seats for whom landing on the target finishes a line. */
  readonly threatFor: readonly number[];
  /** Every empty square, counted. Drawn when the map is switched on. */
  readonly map: readonly ParitySquare[];
  /** The target's column as a fill order — the proof. */
  readonly ladder: readonly LadderStep[];
  /** What the "odd rows belong to player one" shorthand says about the target. */
  readonly rowRule: Seat | null;
  /** The count the player gave, once given. */
  readonly counted: number | null;
  readonly countMisses: number;
  /** The seat the player named, once named. */
  readonly owned: Seat | null;
  readonly ownerMisses: number;
  readonly showMap: boolean;
  /** Discs played in the tempo step. */
  readonly explored: number;
  /**
   * The column the last exploring disc went into.
   *
   * Derived from the two boards rather than remembered, so undo and reset
   * cannot leave it describing a move that is no longer on the board.
   */
  readonly lastColumn: number | null;
  /** Which position of the run, 1-based. */
  readonly positionNumber: number;
  readonly total: number;
  /** Positions where both questions were right first time. */
  readonly correct: number;
  /** The largest count the board can produce, for the answer buttons. */
  readonly maxGaps: number;
  readonly nextReady: boolean;
  readonly error: string | null;
}

interface TrainerState {
  readonly spec: GameSpec;
  readonly phase: ParityPhase;
  readonly position: ParityPosition | null;
  readonly game: State<Rules>;
  /** Positions passed through while exploring, newest last, for undo. */
  readonly history: readonly State<Rules>[];
  readonly counted: number | null;
  readonly countMisses: number;
  readonly owned: number | null;
  readonly ownerMisses: number;
  readonly showMap: boolean;
  /** Set when the whole-board map was opened with a question still open. */
  readonly shown: boolean;
  readonly positionNumber: number;
  readonly correct: number;
  /** Hunted ahead of time, waiting for the next position. */
  readonly queued: ParityPosition | null;
  /** Rises whenever the question changes, to void in-flight replies. */
  readonly token: number;
  readonly seed: number;
  readonly error: string | null;
}

type Action =
  | { readonly type: 'begin' }
  | { readonly type: 'found'; readonly position: ParityPosition | null }
  | { readonly type: 'count'; readonly value: number }
  | { readonly type: 'own'; readonly seat: number }
  | { readonly type: 'explore'; readonly column: number }
  | { readonly type: 'undo' }
  | { readonly type: 'restore' }
  | { readonly type: 'map' }
  | { readonly type: 'next' }
  | { readonly type: 'failed'; readonly message: string };

function emptyGame(spec: GameSpec): State<Rules> {
  return createInitial(spec.rules, spec.rules.seats[0] as SeatOf<Rules>);
}

/** Replay a hunted position, falling back to an empty board on bad input. */
function boardOf(spec: GameSpec, position: ParityPosition | null): State<Rules> {
  if (position === null) return emptyGame(spec);
  return replayParity(spec.rules, position.firstSeat, position.setup) ?? emptyGame(spec);
}

/** The column that changed between two boards, or null when nothing did. */
function columnBetween(rules: Rules, before: State<Rules>, after: State<Rules>): number | null {
  for (let index = 0; index < after.board.length; index += 1) {
    if (before.board[index] !== after.board[index]) return index % rules.board.cols;
  }
  return null;
}

export function createTrainer(spec: GameSpec, seed = 1): TrainerState {
  return {
    spec,
    phase: 'reading',
    position: null,
    game: emptyGame(spec),
    history: [],
    counted: null,
    countMisses: 0,
    owned: null,
    ownerMisses: 0,
    showMap: false,
    shown: false,
    positionNumber: 0,
    correct: 0,
    queued: null,
    token: 0,
    seed,
    error: null,
  };
}

/**
 * Put a hunted position on the board and ask the first question.
 *
 * Clears the queue as it goes: a prefetched position that stayed queued after
 * being mounted would make the screen think it already had the *next* one, and
 * the hunt for it would never be asked for.
 */
function mount(state: TrainerState, position: ParityPosition): TrainerState {
  return {
    ...state,
    position,
    queued: null,
    phase: 'counting',
    game: boardOf(state.spec, position),
    history: [],
    counted: null,
    countMisses: 0,
    owned: null,
    ownerMisses: 0,
    shown: false,
    positionNumber: state.positionNumber + 1,
    token: state.token + 1,
    error: null,
  };
}

export function trainerReducer(state: TrainerState, action: Action): TrainerState {
  const rules = state.spec.rules;

  switch (action.type) {
    case 'begin':
      if (state.phase !== 'reading') return state;
      return state.queued
        ? mount(state, state.queued)
        : { ...state, phase: 'seeking', token: state.token + 1 };

    case 'found': {
      if (action.position === null) {
        return state.phase === 'seeking' ? { ...state, phase: 'stuck' } : state;
      }
      // A position that arrives while one is already up is the prefetch for the
      // next question, not a replacement for the board being looked at.
      if (state.phase !== 'seeking') {
        return state.queued === null ? { ...state, queued: action.position } : state;
      }
      return mount(state, action.position);
    }

    /*
     * Both answers are judged against the board on screen, never against the
     * number the hunt stored. They agree — a test says so — but the board is
     * the thing the player is looking at, and it is the only honest authority
     * for a question about what is in front of them.
     */
    case 'count': {
      if (state.phase !== 'counting' || state.position === null) return state;
      const truth = gapsBeneath(rules, state.game, state.position.target);
      if (action.value !== truth) return { ...state, countMisses: state.countMisses + 1 };
      return { ...state, counted: action.value, phase: 'owning' };
    }

    case 'own': {
      if (state.phase !== 'owning' || state.position === null) return state;
      const truth = ownerOf(rules, state.game, state.position.target);
      if (action.seat !== truth) return { ...state, ownerMisses: state.ownerMisses + 1 };
      const unaided = state.countMisses === 0 && state.ownerMisses === 0 && !state.shown;
      return {
        ...state,
        owned: action.seat,
        phase: 'proving',
        correct: state.correct + (unaided ? 1 : 0),
      };
    }

    /*
     * The tempo step. A disc is played for whichever seat is to move, because
     * that is the mechanism: the turn passing is what hands the square over,
     * and hiding the opponent's replies would hide the cause.
     */
    case 'explore': {
      if (state.phase !== 'proving' && state.phase !== 'playing') return state;
      if (state.game.status.kind !== 'in_play') return state;
      const applied = applyMove(rules, state.game, state.game.toMove, { param: action.column });
      if (!applied.ok) return state;
      return {
        ...state,
        phase: 'playing',
        game: applied.state,
        history: [...state.history, state.game],
      };
    }

    case 'undo': {
      const previous = state.history.at(-1);
      if (previous === undefined) return state;
      const history = state.history.slice(0, -1);
      return {
        ...state,
        game: previous,
        history,
        phase: history.length === 0 ? 'proving' : 'playing',
      };
    }

    case 'restore': {
      if (state.history.length === 0) return state;
      return {
        ...state,
        game: boardOf(state.spec, state.position),
        history: [],
        phase: 'proving',
      };
    }

    /*
     * The whole-board map answers the open question as a side effect: it writes
     * the count on every square, including the ringed one. That is a legitimate
     * thing to want — it is how the method is actually used — so it stays
     * available, and asking for it costs exactly what "show me" costs
     * everywhere else here: the position stops counting as unaided.
     */
    case 'map': {
      const showMap = !state.showMap;
      const asking = state.phase === 'counting' || state.phase === 'owning';
      return { ...state, showMap, shown: state.shown || (showMap && asking) };
    }

    case 'next': {
      if (state.positionNumber >= POSITIONS_PER_RUN) {
        return { ...state, phase: 'finished', token: state.token + 1 };
      }
      return state.queued
        ? mount(state, state.queued)
        : { ...state, phase: 'seeking', token: state.token + 1 };
    }

    case 'failed':
      return { ...state, phase: state.phase === 'seeking' ? 'stuck' : state.phase, error: action.message };
  }
}

export interface UseParityOptions {
  readonly spec: GameSpec;
  /** Runs the engine. Defaults to a real worker; tests pass their own. */
  readonly createWorker?: () => Worker;
  readonly budgetMs?: number;
}

export interface ParityControls {
  readonly view: ParityView;
  readonly begin: () => void;
  readonly count: (value: number) => void;
  readonly own: (seat: number) => void;
  readonly explore: (column: number) => void;
  readonly undo: () => void;
  readonly restore: () => void;
  readonly toggleMap: () => void;
  readonly next: () => void;
}

export function useParity(options: UseParityOptions): ParityControls {
  const { spec } = options;
  const [state, dispatch] = useReducer(trainerReducer, undefined, () => createTrainer(spec));

  const workerRef = useRef<Worker | null>(null);
  const create = options.createWorker;

  useEffect(() => {
    const worker = create
      ? create()
      : new Worker(new URL('./parity.worker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;

    const onMessage = (event: MessageEvent<ParityResponse>) => {
      const message = event.data;
      if (message.kind === 'found') dispatch({ type: 'found', position: message.position });
      else dispatch({ type: 'failed', message: message.message });
    };
    worker.addEventListener('message', onMessage);

    return () => {
      worker.removeEventListener('message', onMessage);
      worker.terminate();
      workerRef.current = null;
    };
  }, [create]);

  /*
   * Hunt during the method card as well as after it.
   *
   * The hunt is fast, but the reading pause is free time either way, and
   * spending it means the first board is already there when the player asks for
   * one. The same request covers the prefetch for the next position.
   */
  const requested = useRef<string | null>(null);

  useEffect(() => {
    const worker = workerRef.current;
    if (!worker) return;

    const wanted =
      state.phase === 'reading' || state.phase === 'seeking'
        ? state.positionNumber + 1
        : state.queued === null && state.positionNumber < POSITIONS_PER_RUN
          ? state.positionNumber + 1
          : null;
    if (wanted === null) return;

    const key = `${String(state.token)}:${String(wanted)}`;
    if (requested.current === key) return;
    requested.current = key;

    const request: FindRequest = {
      kind: 'find',
      rules: spec.rules,
      seed: state.seed * 7919 + wanted * 31,
      ...(options.budgetMs === undefined ? {} : { budgetMs: options.budgetMs }),
      token: state.token,
    };
    worker.postMessage(request);
  }, [spec, state.phase, state.token, state.seed, state.positionNumber, state.queued, options.budgetMs]);

  const view = useMemo<ParityView>(() => {
    const rules = spec.rules;
    const position = state.position;
    const target = position?.target ?? null;
    const live = state.game.status.kind === 'in_play';
    const answered = state.phase === 'proving' || state.phase === 'playing';

    const gaps = target === null ? null : gapsBeneath(rules, state.game, target);
    const owner = target === null || !live ? null : ownerOf(rules, state.game, target);
    const occupied =
      target === null ? false : state.game.board[target] !== rules.cells.empty;

    return {
      spec,
      phase: state.phase,
      board: state.game.board as readonly number[],
      yourSeat: (position?.seat as Seat | undefined) ?? null,
      toMove: live ? (state.game.toMove as Seat) : null,
      // The board is only a place to drop discs during the tempo step; while a
      // question is open it is a diagram, and seven live buttons on a diagram
      // are seven ways to answer something nobody asked.
      legalColumns:
        answered && live ? legalMoves(rules, state.game).map((move) => move.param) : [],
      target,
      targetRow: target === null ? null : Math.floor(target / rules.board.cols),
      targetCol: target === null ? null : target % rules.board.cols,
      gaps: gaps === null || gaps < 0 ? null : gaps,
      owner: (owner as Seat | null) ?? null,
      openingOwner: (position?.owner as Seat | undefined) ?? null,
      takenBy: occupied && target !== null ? ((state.game.board[target] ?? null) as Seat | null) : null,
      threatFor: position?.threatFor ?? [],
      map: state.showMap ? parityMap(rules, state.game) : [],
      ladder: answered && target !== null ? fillLadder(rules, state.game, target) : [],
      rowRule: target === null ? null : ((rowRuleOwner(rules, target) as Seat | null) ?? null),
      counted: state.counted,
      countMisses: state.countMisses,
      owned: (state.owned as Seat | null) ?? null,
      ownerMisses: state.ownerMisses,
      showMap: state.showMap,
      explored: state.history.length,
      lastColumn: (() => {
        const previous = state.history.at(-1);
        return previous === undefined ? null : columnBetween(rules, previous, state.game);
      })(),
      positionNumber: state.positionNumber,
      total: POSITIONS_PER_RUN,
      correct: state.correct,
      maxGaps: rules.board.rows - 1,
      nextReady: state.queued !== null,
      error: state.error,
    };
  }, [spec, state]);

  return {
    view,
    begin: useCallback(() => dispatch({ type: 'begin' }), []),
    count: useCallback((value: number) => dispatch({ type: 'count', value }), []),
    own: useCallback((seat: number) => dispatch({ type: 'own', seat }), []),
    explore: useCallback((column: number) => dispatch({ type: 'explore', column }), []),
    undo: useCallback(() => dispatch({ type: 'undo' }), []),
    restore: useCallback(() => dispatch({ type: 'restore' }), []),
    toggleMap: useCallback(() => dispatch({ type: 'map' }), []),
    next: useCallback(() => dispatch({ type: 'next' }), []),
  };
}

/** The other seat, for the "whose square is it" buttons. */
export function otherSeat(spec: GameSpec, seat: Seat | null): Seat | null {
  if (seat === null) return null;
  return (opponentOf(spec.rules, seat) as Seat | null) ?? null;
}
